import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import {
  addFavoriteTransfer,
  fetchScheduleBookmarkTransfer,
  removeFavoriteTransfer,
  validateBIFastTransfer,
  validateFavoriteTransfer,
} from '@/api/TransferApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import { ISimpleAccountResponse } from '@/domains/AccountDomain'
import { OKSign, SCHEDULE_PAYMENT_ROUTES, STATUS_CODE } from '@/domains/CommonDomain'
import {
  SchedulePaymentHistoryParams,
  SchedulePaymentTransactionPurposeType,
} from '@/domains/SchedulePaymentDomain'
import {
  CreditAccountKindCode,
  IAddFavoriteTransfer,
  IInquiryRecentBookmarkTransferResponse,
  IRemoveFavoriteTransfer,
  IValidateRecentlyFavoriteTransferBIFastRequest,
  TransactionType,
} from '@/domains/TransferDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { VERSION } from '@/modules/common/version'
import { profileSelector } from '@/modules/profile/ProfileModule'
import {
  ISchedulePaymentRegisterState,
  schedulePaymentRegisterActions,
  schedulePaymentRegisterSelector,
} from '@/modules/schedule-payment/SchedulePaymentRegisterModule'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import { IBankInfoResult } from '@/utils/bridgeUtils/features/getBank'
import { IAccountInfoResult } from '@/utils/bridgeUtils/features/getMyAccount'
import {
  addRecentToBookMarks,
  moveBookmarkToRecents,
  removeFromBookmarkAndRecents,
} from '@/utils/favoriteUtils'
import { checkFromRegister, goBack, push } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { apiFailureReducer, apiSuccessReducer } from '@/utils/reducerUtils'

export type ISchedulePaymentSendState = Readonly<{
  scheduleBookmarkTransfer: IInquiryRecentBookmarkTransferResponse
}>

export const SCHEDULE_PAYMENT_SEND = 'SchedulePaymentSend'

const SCHEDULE_PAYMENT_SEND_STATE = `${SCHEDULE_PAYMENT_SEND}/state`
const SCHEDULE_PAYMENT_FAVORITE_ADD = `${SCHEDULE_PAYMENT_SEND}/favorite/add`
const SCHEDULE_PAYMENT_FAVORITE_REMOVE = `${SCHEDULE_PAYMENT_SEND}/favorite/remove`
const SET_MY_ACCOUNT_PAYMENT_STATE = `${SCHEDULE_PAYMENT_SEND}/myAccountPaymentState`
const SET_BANK_ACCOUNT_PAYMENT_STATE = `${SCHEDULE_PAYMENT_SEND}/bankAccountPaymentState`
const SET_HISTORY_PAYMENT_STATE = `${SCHEDULE_PAYMENT_SEND}/setHistoryPaymentState`
const REMOVE_BOOKMARK_AND_RECENTLY_ITEM = `${SCHEDULE_PAYMENT_SEND}/removeBookmarkAndRecentlyItem`
const CLOSE_INVALID_ACCOUNT_LAYER = `${SCHEDULE_PAYMENT_SEND}/closeInvalidAccountLayer`

export const initialState: ISchedulePaymentSendState = {
  scheduleBookmarkTransfer: {
    recents: [],
    bookmarks: [],
  },
}

export const schedulePaymentSendActions = {
  setMyAccountPaymentState: createAction(SET_MY_ACCOUNT_PAYMENT_STATE),
  setBankAccountPaymentState: createAction(SET_BANK_ACCOUNT_PAYMENT_STATE),
  setHistoryPaymentState: createAction(SET_HISTORY_PAYMENT_STATE),
  removeBookmarkAndRecentlyItem: createAction<string>(REMOVE_BOOKMARK_AND_RECENTLY_ITEM),
  closeInvalidAccountLayer: createAction(CLOSE_INVALID_ACCOUNT_LAYER),
}

export const schedulePaymentSendAsync = createFetchAction(SCHEDULE_PAYMENT_SEND_STATE)
export const schedulePaymentFavoriteAddAsync = createPostAction(SCHEDULE_PAYMENT_FAVORITE_ADD)
export const schedulePaymentFavoriteRemoveAsync = createPostAction(SCHEDULE_PAYMENT_FAVORITE_REMOVE)

// 최근 이용 => 즐겨찾기
export const addToFavorite = (
  scheduleBookmarkTransfer: IInquiryRecentBookmarkTransferResponse,
  payload: IAddFavoriteTransfer,
) =>
  addRecentToBookMarks(
    scheduleBookmarkTransfer,
    item => item.trnsAcctKey === payload.trnsAcctKey,
    item => ({ ...item, favYn: OKSign.YES }),
  )

// 즐겨찾기 => 최근 이용
export const removeFromFavorite = (
  scheduleBookmarkTransfer: IInquiryRecentBookmarkTransferResponse,
  payload: IRemoveFavoriteTransfer,
): IInquiryRecentBookmarkTransferResponse =>
  moveBookmarkToRecents(
    scheduleBookmarkTransfer,
    item => item.trnsAcctKey === payload.trnsAcctKey,
    item => ({ ...item, favYn: OKSign.NO }),
  )

const addToBookmarksReducer = (
  state: ISchedulePaymentSendState,
  action: Action<IAddFavoriteTransfer>,
): ISchedulePaymentSendState => {
  const { scheduleBookmarkTransfer: prevScheduleBookmarkTransfer } = state

  return {
    ...state,
    scheduleBookmarkTransfer: addToFavorite(prevScheduleBookmarkTransfer, action.payload),
  }
}

const removeFromBookmarksReducer = (
  state: ISchedulePaymentSendState,
  action: Action<IRemoveFavoriteTransfer>,
): ISchedulePaymentSendState => {
  const { scheduleBookmarkTransfer: prevScheduleBookmarkTransfer } = state

  return {
    ...state,
    scheduleBookmarkTransfer: removeFromFavorite(prevScheduleBookmarkTransfer, action.payload),
  }
}

const bookmarkDeleteReducer = (
  state: ISchedulePaymentSendState,
  action: Action<any>,
): ISchedulePaymentSendState => {
  const { recents, bookmarks } = state.scheduleBookmarkTransfer

  return {
    ...state,
    scheduleBookmarkTransfer: removeFromBookmarkAndRecents(
      { recents, bookmarks },
      item => item.crAcctNo !== action.payload,
    ),
  }
}

const reducer = {
  [schedulePaymentSendAsync.SUCCESS]: apiSuccessReducer,
  [schedulePaymentSendAsync.FAILURE]: apiFailureReducer,
  [schedulePaymentFavoriteAddAsync.SUCCESS]: addToBookmarksReducer,
  [schedulePaymentFavoriteAddAsync.FAILURE]: removeFromBookmarksReducer,
  [schedulePaymentFavoriteRemoveAsync.SUCCESS]: removeFromBookmarksReducer,
  [schedulePaymentFavoriteRemoveAsync.FAILURE]: addToBookmarksReducer,
  [REMOVE_BOOKMARK_AND_RECENTLY_ITEM]: bookmarkDeleteReducer,
}

export const schedulePaymentSendSelector = {
  transferRecents: (state: IRootState) =>
    oc(state[SCHEDULE_PAYMENT_SEND]).scheduleBookmarkTransfer.recents([]),
  transferBookmarks: (state: IRootState) =>
    oc(state[SCHEDULE_PAYMENT_SEND]).scheduleBookmarkTransfer.bookmarks([]),
}

export const schedulePaymentSendReducer = handleActions(reducer, initialState)

export function* fetchSendMain() {
  try {
    const selectedAccount: ISimpleAccountResponse = yield select(
      schedulePaymentRegisterSelector.selectedAccount,
    )
    const scheduleBookmarkTransfer = yield call(
      fetchScheduleBookmarkTransfer,
      selectedAccount.acctNo,
    )

    return { scheduleBookmarkTransfer }
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* schedulePaymentFavoriteAdd(action: Action<IAddFavoriteTransfer>) {
  try {
    yield call(getCSRF)
    // favorite를 UI에 선반영
    yield put(schedulePaymentFavoriteAddAsync.success(action.payload))
    // 서버에 favorite 통보
    yield call(addFavoriteTransfer, action.payload)
  } catch (e) {
    // 에러 발생시 favorite를 롤백
    yield put(schedulePaymentFavoriteAddAsync.failure(action.payload))

    switch (e.code) {
      case STATUS_CODE.MAXIMUM_FAVORITE_COUNT_OVER:
        yield call(Bridge.showNotification, {
          message: $('transferoption_favorite_exceederror_toast'),
        })
        break

      default:
        yield put(errorActions.set(e))
        break
    }
  }
}

function* schedulePaymentFavoriteRemove(action: Action<IRemoveFavoriteTransfer>) {
  try {
    yield call(getCSRF)
    // favorite를 UI에 선반영
    yield put(schedulePaymentFavoriteRemoveAsync.success(action.payload))
    // 서버에 favorite 통보
    yield call(removeFavoriteTransfer, action.payload)
  } catch (e) {
    // 에러 발생시 favorite를 롤백
    yield put(schedulePaymentFavoriteRemoveAsync.failure(action.payload))
    yield put(errorActions.set(e))
  }
}

export function* setBankAccountPaymentState() {
  try {
    yield put(loadingActions.start(SCHEDULE_PAYMENT_SEND))

    const response = yield call(Bridge.getBank)
    console.warn('finGetBank>>', response)
    const result = oc<IBankInfoResult>(response).result(undefined)

    if (!result) {
      return
    }

    const {
      ownerName: bnfcNm,
      bankName: bnfcBankNm,
      accountNumber: bnfcAcctNo,
      bankCode: bnfcBankCd,
      isLineBank,
      accountKindCode: bnfcAcctKindCd,
    } = result
    const trscType = isLineBank ? TransactionType.MY_ACCOUNT : TransactionType.OTHER_BANK
    const { bnfcAcctNo: prevBnfcAcctNo } = yield select(schedulePaymentRegisterSelector.allState)

    if (bnfcAcctNo !== prevBnfcAcctNo) {
      yield put(schedulePaymentRegisterActions.reset())
    }

    yield put(
      schedulePaymentRegisterActions.setState({
        bnfcAcctNo,
        bnfcBankCd,
        bnfcNm,
        bnfcBankNm,
        trscType,
        myAcctYn: OKSign.NO,
        bnfcAcctKindCd,
        ...(!!bnfcAcctKindCd && {
          bnfcAcctItmCd: bnfcAcctNo,
          biPurpCd: SchedulePaymentTransactionPurposeType.FUND_TRANSFER, // default purpose for bi-fast
        }),
      }),
    )
    yield put(schedulePaymentRegisterActions.setInitialAccount())
    const isFromRegister = yield call(checkFromRegister)
    if (isFromRegister) {
      yield call(goBack)
    } else {
      yield call(push, SCHEDULE_PAYMENT_ROUTES.REGISTER)
    }
  } catch (e) {
    switch (e.code) {
      case BRIDGE_ERROR_CODE.CLOSE:
        break

      default:
        yield put(errorActions.set(e))
        break
    }
  } finally {
    yield put(loadingActions.finish(SCHEDULE_PAYMENT_SEND))
  }
}

export function* setHistoryPaymentState(
  action: Action<SchedulePaymentHistoryParams & { trnsAcctKey: number; crBnkBicCd?: string }>,
) {
  try {
    yield put(loadingActions.start(SCHEDULE_PAYMENT_SEND))

    // 상태 초기화
    const { bnfcAcctNo: prevBnfcAcctNo } = yield select(schedulePaymentRegisterSelector.allState)
    if (action.payload.bnfcAcctNo !== prevBnfcAcctNo) {
      yield put(schedulePaymentRegisterActions.reset())
    }
    const { trnsAcctKey, crBnkBicCd, ...restState } = action.payload
    yield put(schedulePaymentRegisterActions.setState(restState))
    yield put(schedulePaymentRegisterActions.setInitialAccount())

    const { version } = yield select(state => state[VERSION])
    if (!version) {
      //v1
      // 벨리데이션 체크
      yield call(validateFavoriteTransfer, {
        trnsAcctKey, // 최근이체계좌key
        crBnkCd: action.payload.bnfcBankCd, // 은행코드번호
        crAcctNo: action.payload.bnfcAcctNo, // 계좌번호
      })
    } else {
      //BI ver
      // 벨리데이션 체크 (BI-Fast / Non BI-Fast)
      const isBIFast = restState.bnfcAcctKindCd === CreditAccountKindCode.BI_FAST_ACCOUNT_NO
      if (isBIFast) {
        // BI-Fast
        yield call(validateBIFastTransfer, {
          crAcctKindDvCd: restState.bnfcAcctKindCd, // 00
          trnsAcctKey,
          crBnkBicCd,
          crAcctItmCd: restState.bnfcAcctItmCd,
        } as IValidateRecentlyFavoriteTransferBIFastRequest)

        yield put(
          schedulePaymentRegisterActions.setState({
            biPurpCd: SchedulePaymentTransactionPurposeType.FUND_TRANSFER,
            bnfcBankCd: crBnkBicCd, // BI-Fast인 경우 BankCode를 bicBankCd로 register 해야하므로 재설정
          }),
        )
      } else {
        // Non BI-Fast
        yield call(validateFavoriteTransfer, {
          trnsAcctKey, // 최근이체계좌key
          crBnkCd: action.payload.bnfcBankCd, // 은행코드번호
          crAcctNo: action.payload.bnfcAcctNo, // 계좌번호
        })
      }

      // 계좌 소유주 정보로 Register State 지정
      yield put(
        schedulePaymentRegisterActions.setStateAccountOwnerInfoAsync({
          crAcctKindDvCd: restState.bnfcAcctKindCd,
          crAcctItmCd: restState.bnfcAcctItmCd,
          crAcctNo: action.payload.bnfcAcctNo,
          crBnkBicCd,
          crBnkCd: action.payload.bnfcBankCd,
        }),
      )
    }

    // 진입경로 체크
    const isFromRegister = yield call(checkFromRegister)
    if (isFromRegister && action.payload.bnfcAcctNo === prevBnfcAcctNo) {
      yield call(goBack)
    } else {
      yield call(push, SCHEDULE_PAYMENT_ROUTES.REGISTER)
    }
  } catch (e) {
    // 계좌번호의 벨리데이션 체크가 실패하면
    if (e.code === STATUS_CODE.INVALID_RECENTLY_FAVORITE_ACCOUNT_NO) {
      // 경고 레이어를 띄운다
      yield put(layerActions.open(LEAVE_ALERT_TYPE.INVALID_RECENTLY_FAVORITE_ACCOUNT_NO))

      return
    }

    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(SCHEDULE_PAYMENT_SEND))
  }
}

export function* setMyAccountPaymentState() {
  try {
    yield put(loadingActions.start(SCHEDULE_PAYMENT_SEND))

    const bnfcNm = yield select(profileSelector.name)
    const { result }: IAccountInfoResult = yield call(Bridge.getMyAccount)

    if (!result) {
      return
    }
    const { acctNo: bnfcAcctNo, acctTitle: bnfcBankNm } = result
    const { bnfcAcctNo: prevBnfcAcctNo } = yield select(schedulePaymentRegisterSelector.allState)

    if (bnfcAcctNo !== prevBnfcAcctNo) {
      yield put(schedulePaymentRegisterActions.reset())
    }

    yield put(
      schedulePaymentRegisterActions.setState({
        bnfcAcctNo,
        bnfcNm,
        bnfcBankNm,
        trscType: TransactionType.MY_ACCOUNT,
        myAcctYn: OKSign.YES,
      }),
    )
    yield put(schedulePaymentRegisterActions.setInitialAccount())
    const isFromRegister = yield call(checkFromRegister)
    if (isFromRegister) {
      yield call(goBack)
    } else {
      yield call(push, SCHEDULE_PAYMENT_ROUTES.REGISTER)
    }
  } catch (e) {
    switch (e.code) {
      case BRIDGE_ERROR_CODE.CLOSE:
        break

      default:
        yield put(errorActions.set(e))
        break
    }
  } finally {
    yield put(loadingActions.finish(SCHEDULE_PAYMENT_SEND))
  }
}

export function* closeInvalidAccountLayer() {
  const { bnfcAcctNo }: ISchedulePaymentRegisterState = yield select(
    schedulePaymentRegisterSelector.allState,
  )
  // 레이어 닫기
  yield put(layerActions.close(LEAVE_ALERT_TYPE.INVALID_RECENTLY_FAVORITE_ACCOUNT_NO))
  // 해당하는 수취계좌번호에 송금하는 아이템을 모두 삭제한다
  yield put(schedulePaymentSendActions.removeBookmarkAndRecentlyItem(bnfcAcctNo))
  // register화면의 상태를 리셋
  yield put(schedulePaymentRegisterActions.reset())
}

export const schedulePaymentSendSaga = [
  takeLatest(schedulePaymentSendAsync.FETCH, createSaga(schedulePaymentSendAsync, fetchSendMain)),
  takeLatest(schedulePaymentFavoriteAddAsync.POST, schedulePaymentFavoriteAdd),
  takeLatest(schedulePaymentFavoriteRemoveAsync.POST, schedulePaymentFavoriteRemove),
  takeLatest(SET_MY_ACCOUNT_PAYMENT_STATE, setMyAccountPaymentState),
  takeLatest(SET_BANK_ACCOUNT_PAYMENT_STATE, setBankAccountPaymentState),
  takeLatest(SET_HISTORY_PAYMENT_STATE, setHistoryPaymentState),
  takeLatest(CLOSE_INVALID_ACCOUNT_LAYER, closeInvalidAccountLayer),
]
